// The Quality page (docs/cockpit/spec.md): the earned-autonomy ladder (M3-21b), where an Owner sees and sets how
// much LAMP does alone and Staff see it read-only, then "What changed in LAMP" (M3-20b): each move of the business's
// LAMP release, newest first, with the plain-language notes about what it runs, and the Ring it is on. A move back
// is a rollback. What changed is for Owners only: the notes speak of every Value Stream the business runs.
import { useEffect, useState } from "react";
import type { Api, WhatChanged } from "../api.js";
import { useWords } from "../i18n/words.js";
import { AutonomyLadder, type LadderApi } from "./AutonomyLadder.js";

export function Quality({ api, owner }: { api: Pick<Api, "releaseChanges"> & LadderApi; owner: boolean }) {
  const w = useWords();
  const [changed, setChanged] = useState<WhatChanged>();
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!owner) return;
    let live = true;
    api.releaseChanges().then(
      (found) => live && setChanged(found),
      () => live && setFailed(true),
    );
    return () => {
      live = false;
    };
  }, [api, owner]);

  return (
    <>
      <AutonomyLadder api={api} owner={owner} />
      <section className="section" aria-labelledby="what-changed-heading">
        <h2 id="what-changed-heading">{w.qualityChangesHeading}</h2>
        {!owner ? (
          <p className="muted">{w.qualityOwnersOnly}</p>
        ) : failed ? (
          <p role="alert">{w.settingsUnreachable}</p>
        ) : !changed ? (
          <p role="status">{w.qualityLoading}</p>
        ) : (
          <>
            <p>{w.qualityOnRelease(changed.release, w.ringName(changed.ring))}</p>
            {changed.changes.length === 0 ? <p className="muted">{w.qualityNoChanges}</p> : null}
            <ul className="plain-list">
              {changed.changes.map((c) => (
                <li key={`${c.at} ${c.release}`}>
                  <h3>{w.qualityRelease(c.release, c.at.slice(0, 10))}</h3>
                  {c.rolledBack ? (
                    <p>{w.qualityRolledBack(c.previous)}</p>
                  ) : c.notes.length === 0 ? (
                    <p className="muted">{w.qualityNothingForYou}</p>
                  ) : (
                    <ul>
                      {c.notes.map((note) => (
                        <li key={note}>{note}</li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          </>
        )}
      </section>
    </>
  );
}
